import TaggedText from "./TaggedText";
import { GENE, VARIANT, REGION } from "../constants/entities";
import { DBS } from "../constants/constants";
import { varsomeHostUrl } from "../../../common/helpers/Urls";
import Tag from "./Tag";

const dateConf = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

class Article {
  constructor(data = {}) {
    const abstractEls = data.abstractEls || [];
    const titleEls = data.titleEls || [];
    const title = data.title || "";
    const abstract = data.abstract || "";
    const tags = (data.tags || []).map((x) => new Tag(x));
    const ttlTags = tags.filter((x) => x.position === "title");
    const absTags = tags.filter((x) => x.position === "abstract");
    const tagsInd = tags.reduce((a, i) => a.set(i.typeKey, i), new Map());
    const uniqueTags = Array.from(tagsInd.values());
    const pDate = new Date(data.publicationDate);
    const sources = data.sources || [];

    this.id = data?.id;
    this.title = new TaggedText(title, titleEls, ttlTags);
    this.abstract = new TaggedText(abstract, abstractEls, absTags);

    this.pdf = data.pdf;
    this.publicationDate = Number.isNaN(pDate.getTime()) ? null : pDate;
    this.citationCount = data.citationCount ?? null;
    this.isOpenAccess = data.isOpenAccess;
    this.authors = (data.authors || []).filter(Boolean);
    this.pubmedId = String(data.pubmedId);
    this.doi = data.doi;
    this.journalName = data.journalName;
    this.journalVolume = data.journalVolume;
    this.journalPages = data.journalPages;
    this.journalIssue = data.journalIssue;
    this.bookTitle = data.bookTitle;
    this.bookPublisher = data.bookPublisher;
    this.tags = tags;
    this.isSourcesExcluded = data.isSourcesExcluded;
    this.tagged = {};
    this.tagged[GENE] = new Set(data.taggedWithGenes || []);
    this.tagged[VARIANT] = new Set(data.taggedWithVariants || []);
    this.tagged[REGION] = new Set(data.taggedWithRegions || []);
    this.userLinkedVariants = new Set(data.userLinkedVariants || []);
    this.userEntryIds = (data.userEntries || []).map((x) => x.entryId);
    this.uniqueTags = uniqueTags.sort((a, b) => a.sortVal - b.sortVal);
    this.sources = sources.map((x) => DBS[x] || x);
  }

  get mainAuthor() {
    if (this.authors.length === 0) return null;
    if (this.authors.length === 1) return this.authors[0];
    return `${this.authors[0]} et al.`;
  }

  get year() {
    return this.publicationDate ? this.publicationDate.getFullYear() : null;
  }

  get journal() {
    const p = [
      this.journalName ? ` ${this.journalName}` : "",
      this.journalVolume ? `;${this.journalVolume}` : "",
      this.journalIssue ? `(${this.journalIssue})` : "",
      this.journalPages ? `:${this.journalPages}` : "",
      this.bookTitle ? ` ${this.bookTitle}` : "",
      this.bookPublisher ? ` ${this.bookPublisher}` : "",
    ];

    return p.join("");
  }

  get citations() {
    if (this.citationCount === null) return "";
    return this.citationCount === 1
      ? "1 citation"
      : `${this.citationCount} citations`;
  }

  get readableDate() {
    const { publicationDate: dt } = this;
    if (dt === null) return null;
    const year = dt.getFullYear();
    const month = dt.getMonth() + 1;
    const day = dt.getDate();
    const fullDateStr = dt.toLocaleDateString("en-US", dateConf);
    return month === 1 && day === 1 ? year : fullDateStr;
  }

  get journalLong() {
    return [
      this.journalName,
      this.journalVolume ? `Volume: ${this.journalVolume}` : "",
      this.journalIssue ? `Issue: ${this.journalIssue}` : "",
      this.journalPages ? `Pages: ${this.journalPages}` : "",
      this.bookTitle ? ` ${this.bookTitle}` : "",
      this.bookPublisher ? ` ${this.bookPublisher}` : "",
    ]
      .filter(Boolean)
      .join(", ");
  }

  get pubmedLink() {
    return this.pubmedId
      ? `http://www.ncbi.nlm.nih.gov/pubmed/${this.pubmedId}`
      : null;
  }

  get varsomeLink() {
    return this.pubmedId ? `${varsomeHostUrl}/article/${this.pubmedId}` : null;
  }
}

export default Article;
